import { request } from "graphql-request";
import getNextConfig from "next/config";
import {
  QueryGetSingleArticleArgs,
  ArticleSearchInput,
  PageType,
  QueryGetSupportedSportsArgs,
  AuthorSearchInput,
  ContentAuthorAuthoredContentArgs,
  QueryGetHomepageArgs,
  QueryGetProjectionTableMetadataArgs,
  QueryGetSlatesArgs,
  ProjectionsInput,
  PositionAbbrev,
  ShortFormNewsTypeEnum,
  ShortFormSearchInput,
  PromotionsInput,
  GetSingleAuthorQueryQueryVariables,
  NflScheduleSeasonTypeEnum,
  SportEnum,
  InputMaybe,
  TimestampBoundaries,
} from "@/graphql/gql/graphql";
import getActiveFixturesNflQuery from "@/graphql/queries/getActiveFixturesNflQuery";
import getActiveFixturesQuery from "@/graphql/queries/getActiveFixturesQuery";
import getAllTentpoleEvents from "@/graphql/queries/getAllTentpoleEventsQuery";
import getArticleBySlugQuery from "@/graphql/queries/getArticleBySlugQuery";
import getArticleInfoByDateRangeQuery from "@/graphql/queries/getArticleInfoByDateRangeQuery";
import getArticlesQuery from "@/graphql/queries/getArticlesQuery";
import getAuthorsQuery from "@/graphql/queries/getAuthorsQuery";
import getDivisionStandingsQuery from "@/graphql/queries/getDivisionStandingsQuery";
import getEfficiencyQuery from "@/graphql/queries/getEfficiencyQuery";
import getEventSummariesQuery from "@/graphql/queries/getEventSummariesQuery";
import getFeaturedArticles from "@/graphql/queries/getFeaturedArticles";
import getGameInjuriesQuery from "@/graphql/queries/getGameInjuriesQuery";
import getGameLeadersQuery from "@/graphql/queries/getGameLeadersQuery";
import getGameLogQuery from "@/graphql/queries/getGameLogQuery";
import getGameScoresQuery from "@/graphql/queries/getGameScoresQuery";
import getGolfEventsQuery from "@/graphql/queries/getGolfEventsQuery";
import { getHeadToHeadTopLevelDataQuery } from "@/graphql/queries/getHead2HeadTopLevelDataQuery";
import getHeadtoHeadBetSuccessQuery from "@/graphql/queries/getHeadToHeadBetSuccessQuery";
import getHeadToHeadFantasyProjectionsQuery from "@/graphql/queries/getHeadToHeadFantasyProjectionsQuery";
import getHeadToHeadHeaderQuery from "@/graphql/queries/getHeadToHeadHeaderQuery";
import getHeadToHeadOddsQuery from "@/graphql/queries/getHeadToHeadOddsQuery";
import getHeadToHeadPlayerGameLogQuery from "@/graphql/queries/getHeadToHeadPlayerGameLogQuery";
import getHeadToHeadPlayerGameStatsQuery from "@/graphql/queries/getHeadToHeadPlayerGameStatsQuery";
import getHeadToHeadPlayerPropsQuery from "@/graphql/queries/getHeadToHeadPlayerPropsQuery";
import getHeadToHeadPlayerStatsQuery from "@/graphql/queries/getHeadToHeadPlayerStatsQuery";
import getHeadToHeadPopularBetsQuery from "@/graphql/queries/getHeadToHeadPopularBetsQuery";
import getHeadToHeadTeamStatsQuery from "@/graphql/queries/getHeadToHeadTeamStatsQuery";
import getHomepageQuery from "@/graphql/queries/getHomepageQuery";
import getHorseRacesByTrackQuery from "@/graphql/queries/getHorseRacesByTrackQuery";
import getHorseRacesQuery from "@/graphql/queries/getHorseRacesQuery";
import getHorseTrackListQuery from "@/graphql/queries/getHorseTrackListQuery";
import getLiveCoreOddsQuery from "@/graphql/queries/getLiveCoreOdds";
import getMenusQuery from "@/graphql/queries/getMenus";
import getNflScheduleGamesQuery from "@/graphql/queries/getNflScheduleGamesQuery";
import getPlayerNewsHomeQuery from "@/graphql/queries/getPlayerNewsHome";
import getSportsPN from "@/graphql/queries/getPlayerNewsSports";
import getProjectionMetaDataQuery from "@/graphql/queries/getProjectionMetaDataQuery";
import getProjectionsQuery from "@/graphql/queries/getProjectionsQuery";
import { getPromotionsQuery } from "@/graphql/queries/getPromotionsQuery";
import getRaceByTrackQuery from "@/graphql/queries/getRaceByTrackQuery";
import getRedirectQuery from "@/graphql/queries/getRedirectQuery";
import getRunnerStatsQuery from "@/graphql/queries/getRunnerStatsQuery";
import getShortFormQuery from "@/graphql/queries/getShortFormQuery";
import getShortFormsQuery from "@/graphql/queries/getShortFormsQuery";
import getSingleAuthorQuery from "@/graphql/queries/getSingleAuthorQuery";
import getSitemapShortFormsQuery from "@/graphql/queries/getSitemapShortFormsQuery";
import getSiteSettingsQuery from "@/graphql/queries/getSiteSettingsQuery";
import getSlatesQuery from "@/graphql/queries/getSlatesQuery";
import getSubCategoriesQuery from "@/graphql/queries/getSubCategoriesQuery";
import getSubCategoryQuery from "@/graphql/queries/getSubCategoryQuery";
import getSingleSportBetsQuery from "@/graphql/queries/getSupportedSports/getSingleSportBets";
import getSports from "@/graphql/queries/getSupportedSports/getSports";
import getSupportedSportsFilters from "@/graphql/queries/getSupportedSports/getSupportedSportsFilters";
import getTentpoleEventQuery from "@/graphql/queries/getTentpoleEventQuery";
import getTrackDetailsQuery from "@/graphql/queries/getTrackDetailsQuery";
import getTrackRacesQuery from "@/graphql/queries/getTrackRacesQuery";
import getContentFilters from "../graphql/queries/getContentFilters/getContentFilters";
import getConfig from "./config";

const DEFAULT_TIMEOUT = 15000;

export function WrapperSignalTimeout() {
  return AbortSignal.timeout(DEFAULT_TIMEOUT) as never;
}

export const getOrchestrationEndpoint = () => {
  const { publicRuntimeConfig } = { ...getNextConfig(), ...getConfig() };
  const { ORCHESTRATION_ENDPOINT } = publicRuntimeConfig || {};
  return ORCHESTRATION_ENDPOINT || "http://localhost:3000/api";
};

export function getMenus(isPreview?: boolean) {
  return request({
    url: getOrchestrationEndpoint(),
    variables: { isPreview },
    document: getMenusQuery,
    signal: WrapperSignalTimeout(),
  });
}

export function getArticleBySlug({
  slug,
  contentStatus,
  isPreview,
}: QueryGetSingleArticleArgs) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getArticleBySlugQuery,
    variables: {
      slug,
      contentStatus,
      isPreview,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getAllArticles({
  limit = 10,
  afterCursor = null,
  sportName = null,
  hideDeprioritizedContent = null,
  ...rest
}: ArticleSearchInput) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getArticlesQuery,
    variables: {
      filter: {
        limit,
        sportName,
        afterCursor,
        hideDeprioritizedContent,
        ...rest,
      },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getArticleInfoByDateRange({
  publishedWithin,
  limit = 0,
}: ArticleSearchInput) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getArticleInfoByDateRangeQuery,
    variables: {
      filter: {
        publishedWithin,
        limit,
      },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getArticlesBySport(sports: string[]) {
  return Promise.all(
    sports.map((sportName) =>
      request({
        url: getOrchestrationEndpoint(),
        document: getArticlesQuery,
        variables: {
          filter: {
            sportName,
            afterCursor: "",
          },
        },
        signal: WrapperSignalTimeout(),
      })
    )
  );
}

export function getFeaturedArticlesBySport(sportSlugs: string[]) {
  return Promise.all(
    sportSlugs.map((sportSlug) =>
      request({
        url: getOrchestrationEndpoint(),
        document: getFeaturedArticles,
        variables: {
          filter: {
            sportSlug,
            pageType: sportSlug ? PageType.Sport : PageType.Homepage,
          },
        },
        signal: WrapperSignalTimeout(),
      })
    )
  );
}

export function getSubCategory(sport: string, subCategory: string) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getSubCategoryQuery,
    variables: {
      input: {
        sportName: sport,
        slug: subCategory,
      },
      filter: {
        limit: 16,
        subCategorySlug: subCategory,
      },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getSubCategories() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getSubCategoriesQuery,
    signal: WrapperSignalTimeout(),
  });
}

interface GetShortFormsParams {
  filter: GetShortFormsParamsFilter;
  limit?: number;
}

interface GetShortFormsParamsFilter {
  start?: string;
  end?: string;
  sportName?: string;
  cursor?: string | null;
  teamNumberFireId?: number | null;
  positionAbbrev?: PositionAbbrev | null;
  shortFormNewsType?: ShortFormNewsTypeEnum | null;
  publishedWithin?: InputMaybe<TimestampBoundaries>;
}

export async function getShortForms({ filter, limit }: GetShortFormsParams) {
  try {
    const {
      sportName,
      cursor,
      positionAbbrev,
      shortFormNewsType,
      teamNumberFireId,
      publishedWithin,
    } = filter;

    const queryParams = {
      filter: {
        limit,
        sport: sportName,
        afterCursor: cursor,
        team: {
          numberFireId: teamNumberFireId,
        },
        player: {
          positionAbbrev,
        },
        publishedWithin,
        shortFormNewsType,
      },
    };

    const response = await request({
      url: getOrchestrationEndpoint(),
      document: getShortFormsQuery,
      variables: queryParams,
      signal: WrapperSignalTimeout(),
    });

    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in getShortForms API request:", {
      error,
      url: getOrchestrationEndpoint(),
    });
    throw error;
  }
}

export async function getSitemapShortForms({
  filter,
  limit,
}: GetShortFormsParams) {
  try {
    const {
      sportName,
      cursor,
      positionAbbrev,
      shortFormNewsType,
      teamNumberFireId,
      start,
      end,
    } = filter;

    const publishedWithin =
      start && end
        ? {
            start,
            end,
          }
        : undefined;

    const queryFilter: ShortFormSearchInput = {
      limit,
      sport: sportName,
      afterCursor: cursor,
      team: {
        numberFireId: teamNumberFireId,
      },
      publishedWithin,
      player: {
        positionAbbrev,
      },
      shortFormNewsType,
    };
    const queryParams = {
      filter: queryFilter,
    };

    const response = await request({
      url: getOrchestrationEndpoint(),
      document: getSitemapShortFormsQuery,
      variables: queryParams,
      signal: WrapperSignalTimeout(),
    });

    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in getSitemapShortForms API request:", error);
    throw error;
  }
}

export function getAuthors({
  contentType,
  sportName,
  sport,
  afterCursor,
  limit = 12,
}: AuthorSearchInput & ContentAuthorAuthoredContentArgs) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getAuthorsQuery,
    variables: {
      input: { sportName, afterCursor, limit },
      contentType,
      sport,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getSingleAuthor({ slug }: GetSingleAuthorQueryQueryVariables) {
  return request(getOrchestrationEndpoint(), getSingleAuthorQuery, {
    slug,
  });
}

export function getScores(sport?: string) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getActiveFixturesQuery,
    variables: {
      input: { sport },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getScoresNfl() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getActiveFixturesNflQuery,
    signal: WrapperSignalTimeout(),
  });
}

export function getPlayerNewsHome({ sport, limit = 9 }: ShortFormSearchInput) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getPlayerNewsHomeQuery,
    variables: {
      filter: {
        sport,
        limit,
      },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getAvailableSports({
  isPreview,
  featured,
}: QueryGetSupportedSportsArgs = {}) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getSports,
    variables: {
      isPreview,
      featured,
    },
    signal: WrapperSignalTimeout(),
  });
}
export function getSingleSportBets({ slug }: { slug: string }) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getSingleSportBetsQuery,
    variables: {
      slug,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getAvailableSportsPN({
  isPreview,
  featured,
}: QueryGetSupportedSportsArgs = {}) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getSportsPN,
    variables: {
      isPreview,
      featured,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getRedirect(slug: string) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getRedirectQuery,
    variables: {
      input: { slug },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHomepage({ isPreview }: QueryGetHomepageArgs) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHomepageQuery,
    variables: { isPreview },
    signal: WrapperSignalTimeout(),
  });
}

export function getTentpoleEvent(slug = "", sportSlug = "") {
  return request({
    url: getOrchestrationEndpoint(),
    document: getTentpoleEventQuery,
    variables: {
      input: { slug, sportSlug },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getTentpoleEvents() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getAllTentpoleEvents,
    signal: WrapperSignalTimeout(),
  });
}

export function getProjectionInfo(args: QueryGetProjectionTableMetadataArgs) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getProjectionMetaDataQuery,
    variables: args,
    signal: WrapperSignalTimeout(),
  });
}

export function getSlates(variables: QueryGetSlatesArgs) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getSlatesQuery,
    variables,
    signal: WrapperSignalTimeout(),
  });
}

export function getGolfEvents() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getGolfEventsQuery,
    signal: WrapperSignalTimeout(),
  });
}

export function getProjectionData(args: ProjectionsInput) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getProjectionsQuery,
    variables: {
      input: args,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getShortForm(slug: string) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getShortFormQuery,
    variables: { slug },
    signal: WrapperSignalTimeout(),
  });
}

export function getSupportedSportsFiltersApi() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getSupportedSportsFilters,
    signal: WrapperSignalTimeout(),
  });
}

export function getContentFiltersApi() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getContentFilters,
    signal: WrapperSignalTimeout(),
  });
}

export function getUpcomingHorseRaces({ current = 0, results = 10 }) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHorseRacesQuery,
    variables: {
      profile: "FDR-Generic",
      current,
      results,
    },
    signal: WrapperSignalTimeout(),
  });
}

interface GetRaceByTrackInput {
  trackCode: string;
  raceNumber: number;
}

export function getRaceByTrack({ trackCode, raceNumber }: GetRaceByTrackInput) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getRaceByTrackQuery,
    variables: {
      profile: "FDR-Generic",
      trackCode,
      raceNumber,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getTrackRaces({ trackCode }: { trackCode: string }) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getTrackRacesQuery,
    variables: {
      profile: "FDR-Generic",
      trackCode,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHorseTrackList() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHorseTrackListQuery,
    variables: {
      profile: "FDR-Generic",
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getRunnerStats({ runnerName }: { runnerName: string }) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getRunnerStatsQuery,
    variables: {
      profile: "FDR-Generic",
      runnerName,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getAllRacesByTrack({
  trackCode,
}: Partial<GetRaceByTrackInput>) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHorseRacesByTrackQuery,
    variables: {
      profile: "FDR-Generic",
      trackCode,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getTrackDetails({ trackCode }: { trackCode: string }) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getTrackDetailsQuery,
    variables: {
      profile: "FDR-Generic",
      trackCode,
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getSiteSettings() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getSiteSettingsQuery,
    signal: WrapperSignalTimeout(),
  });
}

export function getPromotions({ tags }: PromotionsInput) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getPromotionsQuery,
    variables: {
      input: { tags },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHeadToHeadTopLevelData(numberFireGameId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadTopLevelDataQuery,
    variables: {
      input: { eventId: numberFireGameId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHeadToHeadPlayerStats(numberFireGameId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadPlayerStatsQuery,
    variables: {
      input: { eventId: numberFireGameId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHeadToHeadTeamStats(numberFireGameId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadTeamStatsQuery,
    variables: {
      input: { eventId: numberFireGameId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHeadToHeadBetSuccess(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadtoHeadBetSuccessQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHeadToHeadFantasyProjections(numberFireGameId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadFantasyProjectionsQuery,
    variables: {
      input: { eventId: numberFireGameId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHeadToHeadPopularBets(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadPopularBetsQuery,
    signal: WrapperSignalTimeout(),
    variables: { input: { eventId } },
  });
}

export function getHeadToHeadPlayerGameLog(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadPlayerGameLogQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getHeadToHeadPlayerGameStats(numberFirePlayerId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadPlayerGameStatsQuery,
    variables: {
      input: {
        sport: SportEnum.Nfl,
        numberFireId: numberFirePlayerId,
      },
    },
    signal: WrapperSignalTimeout(),
  });
}
export function getHeadToHeadPlayerProps(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadPlayerPropsQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}
export function getHeadToHeadHeader(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadHeaderQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}
export function getHeadToHeadOdds(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getHeadToHeadOddsQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getGameInjuries(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getGameInjuriesQuery,
    signal: WrapperSignalTimeout(),
    variables: { input: { eventId } },
  });
}

export function getGameLog(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getGameLogQuery,
    signal: WrapperSignalTimeout(),
    variables: { input: { eventId } },
  });
}

export function getDivisionStandings() {
  return request({
    url: getOrchestrationEndpoint(),
    document: getDivisionStandingsQuery,
    signal: WrapperSignalTimeout(),
  });
}

export function getNflScheduleGames({ week }: { week?: number }) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getNflScheduleGamesQuery,
    variables: {
      input: week
        ? {
            week: week < 19 ? week : week - 18,
            seasonType:
              week < 19
                ? NflScheduleSeasonTypeEnum.Regular
                : NflScheduleSeasonTypeEnum.Post,
          }
        : undefined,
    },
    signal: AbortSignal.timeout(20000) as never,
  });
}

export function getTeamEfficiencyOverview(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getEfficiencyQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getGameLeaderStats(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getGameLeadersQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}

export function getEventSummaries(seasons: number[]) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getEventSummariesQuery,
    variables: { seasons },
    signal: WrapperSignalTimeout(),
  });
}

export function getGameScores(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getGameScoresQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}
export function getLiveCoreOdds(eventId: number) {
  return request({
    url: getOrchestrationEndpoint(),
    document: getLiveCoreOddsQuery,
    variables: {
      input: { eventId },
    },
    signal: WrapperSignalTimeout(),
  });
}
